import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  makeStyles,
} from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const empty = {
  name: "",
  maxUsers: 5,
  maxConnections: 1,
  maxQueues: 5,
  useCampaigns: true,
};

const PlanModal = ({ open, onClose, plan, onSaved }) => {
  const classes = useStyles();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(plan ? { ...empty, ...plan } : empty);
  }, [open, plan]);

  const set = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      maxUsers: Number(form.maxUsers),
      maxConnections: Number(form.maxConnections),
      maxQueues: Number(form.maxQueues),
      useCampaigns: form.useCampaigns,
    };
    try {
      if (plan) {
        await api.put(`/plans/${plan.id}`, payload);
      } else {
        await api.post("/plans", payload);
      }
      toast.success(i18n.t("plans.toasts.saved"));
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const limitField = (field) => (
    <TextField
      label={i18n.t(`planModal.form.${field}`)}
      type="number"
      value={form[field]}
      onChange={(e) => set(field)(e.target.value)}
      variant="outlined"
      size="small"
      inputProps={{ min: 0, max: 9999 }}
      required
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {i18n.t(plan ? "planModal.title.edit" : "planModal.title.add")}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <TextField
            className={classes.field}
            label={i18n.t("planModal.form.name")}
            value={form.name}
            onChange={(e) => set("name")(e.target.value)}
            variant="outlined"
            fullWidth
            required
            autoFocus
          />
          <div className={classes.grid}>
            {limitField("maxUsers")}
            {limitField("maxConnections")}
            {limitField("maxQueues")}
          </div>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={!!form.useCampaigns}
                onChange={(e) => set("useCampaigns")(e.target.checked)}
              />
            }
            label={i18n.t("planModal.form.useCampaigns")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving} variant="outlined">
            {i18n.t("planModal.buttons.cancel")}
          </Button>
          <Button type="submit" color="primary" variant="contained" disabled={saving}>
            {i18n.t("planModal.buttons.ok")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PlanModal;
